/**
 * RiskPanel Component
 *
 * Main panel for displaying validation results from compliance checks.
 * This component separates the results display from the configuration in Compliance Hub.
 *
 * HOW TO ADD NEW RULE CATEGORIES:
 * 1. Add your rule category to the groupResultsByCategory function
 * 2. The component will automatically create a collapsible section for it
 * 3. Results will be filterable and collapsible like existing categories
 *
 * ARCHITECTURE NOTES:
 * - Data flows from rulesStore (validation results)
 * - Compliance Hub handles configuration
 * - Risk Panel displays results only
 * - All results are real-time from the store
 */

import { useEffect, useMemo, useState } from "react";
import { useModelStatistics } from "../../hooks/useModelStatistics";
import { ModelStatisticsService } from "../../services/core/modelStatistics";
import { ViewerService } from "../../services/core/viewerTools";
import { loadComplianceReportsForModel } from "../../services/dataService";
import { useRulesStore } from "../../store/rulesStore";
import { useViewerStore } from "../../store/viewerStore";
import type { ValidationResult } from "../../types/rules";
import { CollapsibleResults } from "../common/CollapsibleResults";
import { ToolsAccordion } from "../common/ToolsAccordion";
import { BoundingBoxIntersectionTool } from "../debug/BoundingBoxIntersectionTool";
import { DistanceMeasurementTool } from "../debug/DistanceMeasurementTool";
import { EnhancedDebugTools as MultiElementVisualizer } from "../debug/EnhancedDebugTools";
import { RayCastingTool } from "../debug/RayCastingTool";
import type { ColorName } from "../debug/utils/visualizationUtils";
import { clearVisualization } from "../debug/utils/visualizationUtils";
import { CollapsibleElementExtraction } from "../extraction/CollapsibleElementExtraction";
import { CollapsibleOrientationTool } from "../orientation/CollapsibleOrientationTool";
import { CollapsiblePropertiesInspector } from "../properties/CollapsiblePropertiesInspector";
import { IssueManagementPanel } from "../rules/IssueManagementPanel";
import { ComplianceReportButton } from "../reports/ComplianceReportButton";

type FilterType = "pass" | "warning" | "fail" | "info";

// File-local copy of naturalSort (source: ValidationReportDisplay.tsx).
// Sorts dotted version-like strings numerically per segment (e.g., 100.1 < 100.2 < 100.10).
const naturalSort = (a: string, b: string): number => {
  const aParts = a.split(".").map((p) => { const n = parseInt(p, 10); return isNaN(n) ? p : n; });
  const bParts = b.split(".").map((p) => { const n = parseInt(p, 10); return isNaN(n) ? p : n; });
  const maxLen = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < maxLen; i++) {
    const ap = aParts[i] ?? 0;
    const bp = bParts[i] ?? 0;
    if (typeof ap === "number" && typeof bp === "number") {
      if (ap !== bp) return ap - bp;
    } else if (typeof ap === "number") return -1;
    else if (typeof bp === "number") return 1;
    else {
      const c = String(ap).localeCompare(String(bp));
      if (c !== 0) return c;
    }
  }
  return 0;
};

// Parse the numeric part of "LEVEL 12" -> 12. Returns +Infinity when missing
// so unknown-level units sort last in ascending order.
const parseLevelNumber = (level: string | undefined | null): number => {
  if (!level) return Number.POSITIVE_INFINITY;
  const m = String(level).match(/\d+/);
  return m ? parseInt(m[0], 10) : Number.POSITIVE_INFINITY;
};

interface ValidationReport {
  // Common fields for both toilet and door reports
  report_type?: 'toilet' | 'door';
  wc_db_id?: number;  // For toilet reports
  door_db_id?: number;  // For door reports
  door_type?: string;  // e.g., "swinging"
  door_type_name?: string;  // e.g., "A - 3' 0\" x 8' 0\""
  room_name?: string;
  room_number?: string;
  // Old format
  toilet_element_id?: string | number;
  validation_timestamp?: string;
  jurisdiction?: string;
  standard?: string;
  standard_section?: string;
  compartment_type_detection?: {
    detected_type?: string;
    detection_method?: string;
    confidence?: string;
    notes?: string;
  };
  regulations?: Array<{
    regulation_id: string;
    section: string;
    title: string;
    requirement?: string;
    description?: string;
    status: string;
    measured_value?: string | null;
    required_value?: string;
    jersey_specific?: boolean;
    notes?: string;
    confidence?: string;
    severity?: string | null;
    entities_used?: Array<{
      entity_name?: string;
      dbId?: number;
      elementId?: number;
      [key: string]: any;
    }>;
  }>;
  summary?: {
    total_regulations?: number;
    passed_count?: number;
    failed_count?: number;
    not_tested_count?: number;
  };
  // New v3 format
  metadata?: {
    element_id?: number;
    element_name?: string;
    element_type?: string;
    validation_date?: string;
    standard?: string;
    jurisdiction?: string;
    section?: string;
  };
  // New v4 format
  validation_metadata?: {
    room_id?: string | number;
    room_name?: string;
    standard?: string;
    jurisdiction?: string;
    section?: string;
    validation_date?: string;
  };
  entity_mapping?: {
    water_closet?: { dbId?: number; name?: string; type?: string };
    side_wall?: {
      dbId?: number;
      name?: string;
      type?: string;
      location?: string;
    };
    rear_wall?: {
      dbId?: number;
      name?: string;
      type?: string;
      location?: string;
    };
    lavatory?: { dbId?: number; name?: string; type?: string };
    shower?: { dbId?: number; name?: string; type?: string };
    tub?: { dbId?: number; name?: string; type?: string };
    grab_bars?: {
      rear_wall?: { dbId?: number; name?: string };
      side_wall_horizontal?: { dbId?: number; name?: string };
      side_wall_vertical?: { dbId?: number; name?: string };
    };
    other_entities?: Array<{
      dbId?: number;
      name?: string;
      type?: string;
      category?: string;
    }>;
  };
  validation_results?: Array<{
    regulation_id: string;
    section: string;
    title: string;
    description?: string;
    requirement?: string;
    status: string;
    measured_value?: string | null;
    required_value?: string;
    jersey_specific?: boolean;
    notes?: string;
    confidence?: string;
    severity?: string | null;
    entities_used?: Array<{
      entity_name?: string;
      dbId?: number;
      elementId?: number;
      [key: string]: any;
    }>;
  }>;
  // v5 format (rule_validations) - used by door and toilet compliance reports
  rule_validations?: Array<{
    rule_number: string;
    rule_name: string;
    rule_raw_text?: string;
    status: string;
    warning_type?: string | null;
    warning_message?: string | null;
    involved_db_ids?: number[];
    involved_elements?: any[];
    measured_value?: number | string | null;
    required_value?: string | null;
    unit?: string | null;
    measurement_method?: string | null;
    measurement_accuracy?: string | null;
    used_tool_measurement?: boolean;
    llm_reasoning?: string | null;
    llm_warnings?: string[];
    llm_notes?: string[];
    confidence?: string;
    visible?: boolean;
    not_applicable_reason?: string | null;
    not_tested_reason?: string | null;
    actual_value?: string | null;
    details?: string;
    evidence?: string[];
    remediation?: string | null;
    sub_checks?: any[];
  }>;
  // v4 format
  regulation_validation_results?: Array<{
    regulation_id: string;
    section?: string;
    title?: string;
    status: string;
    measured_value?: string | null;
    required_value?: string;
    notes?: string;
    confidence?: string;
    entities_used?: Array<{
      entity_name?: string;
      dbId?: number;
      elementId?: number;
      [key: string]: any;
    }>;
    visible?: boolean;
  }>;
}

// Strip text after and including underscore from regulation ID for display
const formatRegulationIdForDisplay = (regulationId: string): string => {
  const underscoreIndex = regulationId.indexOf("_");
  if (underscoreIndex !== -1) {
    return regulationId.substring(0, underscoreIndex);
  }
  return regulationId;
};

// Get parent section title for specific regulation IDs
const getParentSectionTitle = (regulationId: string): string | null => {
  // Map specific regulation IDs to their parent section titles
  if (regulationId.startsWith("1103.11.1")) {
    return "Toilet and Bathing facilities";
  }
  // Add more mappings as needed
  return null;
};

interface RiskPanelProps {
  onNavigateToRisks?: () => void;
}

export function RiskPanel({ onNavigateToRisks }: RiskPanelProps = {}) {
  const {
    validationResults: storeValidationResults,
    validationSummary,
    ruleTemplates,
    availableUsers,
    loadUsers,
  } = useRulesStore();
  const { viewer, isModelLoaded } = useViewerStore();
  const {
    statistics: modelStats,
    isLoading: statsLoading,
    error: statsError,
  } = useModelStatistics();

  // State for validation reports
  const [validationReports, setValidationReports] = useState<
    ValidationReport[]
  >([]);
  const [isLoadingReports, setIsLoadingReports] = useState(true);
  const [expandedToilets, setExpandedToilets] = useState<Set<string>>(
    new Set()
  );
  // Track expansion state of the inner ADA 604 accordion per toilet
  const [expandedAdaToilets, setExpandedAdaToilets] = useState<Set<string>>(
    new Set()
  );
  // Skylight: per-rule inner accordion state. Key = `${unitName}::${ruleNumber}`.
  const [expandedSkylightRules, setExpandedSkylightRules] = useState<Set<string>>(
    new Set()
  );

  // State for filters - all enabled by default (including info)
  const [activeFilters, setActiveFilters] = useState<Set<FilterType>>(
    new Set(["pass", "warning", "fail", "info"])
  );

  // State for expanded categories - all expanded by default
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );

  // State for issue management
  const [selectedIssueForManagement, setSelectedIssueForManagement] =
    useState<ValidationResult | null>(null);

  // View mode: "unit" (by room_name), "regulation" (by chapter), or "rule"
  // (flat list of rules — skylight default)
  const [viewMode, setViewMode] = useState<"unit" | "regulation" | "rule">(
    "regulation"
  );

  // Load validation reports from grouped folder (using GCS service)
  const { selectedModelId, selectedJurisdiction } = useViewerStore();

  // Skylight: default to the flat by-rule view (3 rule headers, each collapsed).
  useEffect(() => {
    if (selectedModelId === 'skylight') setViewMode('rule');
  }, [selectedModelId]);

  // This loads both toilet compliance reports and door compliance reports
  useEffect(() => {
    const loadValidationReports = async () => {
      setIsLoadingReports(true);
      try {
        const reports = await loadComplianceReportsForModel<ValidationReport>(selectedModelId, selectedJurisdiction);

        // Skylight: translate involved_revit_ids → Forge dbIds using the static
        // mapping file (instant, no Forge API calls needed).
        if (selectedModelId === 'skylight') {
          try {
            const mapResp = await fetch('/resources/results/skylight/skylight_revit_to_dbid_with_family.json');
            if (mapResp.ok) {
              const revitMap: Record<string, { dbId: number; category?: string; familyName?: string; typeName?: string }> = await mapResp.json();
              let translated = 0;
              for (const report of reports as any[]) {
                for (const v of (report.rule_validations || [])) {
                  const revitIds: string[] = Array.isArray(v.involved_revit_ids) ? v.involved_revit_ids : [];
                  if (revitIds.length > 0 && (!v.involved_db_ids || v.involved_db_ids.length === 0)) {
                    const dbIds: number[] = [];
                    for (const rid of revitIds) {
                      const entry = revitMap[rid];
                      if (entry?.dbId != null) dbIds.push(entry.dbId);
                    }
                    if (dbIds.length > 0) {
                      v.involved_db_ids = dbIds;
                      translated++;
                    }
                  }
                }
              }
              console.log(`[Skylight] Translated ${translated} validations via static mapping`);
            }
          } catch (err) {
            console.warn('[Skylight] Revit ID mapping load failed:', err);
          }
        }

        setValidationReports(reports);
      } catch (error) {
        console.error("Error loading validation reports:", error);
      } finally {
        setIsLoadingReports(false);
      }
    };

    loadValidationReports();
  }, [selectedModelId, selectedJurisdiction, viewer, isModelLoaded]);

  // Convert validation reports to ValidationResult format
  const validationResults = useMemo(() => {
    const results: ValidationResult[] = [];

    validationReports.forEach((report) => {
      // Handle v5 format (rule_validations), v4 format (regulation_validation_results), v3 format (validation_results), and old format (regulations)
      const regulations =
        report.rule_validations ||
        report.regulation_validation_results ||
        report.validation_results ||
        report.regulations ||
        [];
      if (regulations.length === 0) return;

      // Determine if this is a door report or toilet report
      const isDoorReport = report.report_type === 'door' || !!report.door_db_id;

      // Get element ID from door_db_id, wc_db_id, or other formats
      const elementDbId = report.door_db_id || report.wc_db_id
        ? String(report.door_db_id || report.wc_db_id)
        : report.validation_metadata?.room_id
          ? String(report.validation_metadata.room_id)
          : report.metadata?.element_id
            ? String(report.metadata.element_id)
            : String(report.toilet_element_id || "unknown");

      regulations.forEach((regulation) => {
        // Filter by visible field - only hide if explicitly set to false
        // If visible is false, skip this regulation; otherwise show it (true or missing)
        if (regulation.visible === false) {
          return; // Skip this regulation
        }

        // Filter out all 604.8.* rules (hidden by request)
        const ruleNumber = regulation.rule_number || regulation.regulation_id || "";
        if (ruleNumber.startsWith("604.8")) {
          return; // Skip this regulation
        }

        // BNE: only show regulations matching Compliance Hub allowlist
        if (selectedModelId === 'bne') {
          const bneAllowedPrefixes = [
            '604.2.2',
            '604.5.1.1', '604.5.1.2.3', '604.5.2',
            '1103.11.1', '1103.11.2.4.1', '1103.11.2.4.4',
            '404.2.2', '404.2.3.2',
          ];
          const isAllowed = bneAllowedPrefixes.some(p =>
            ruleNumber === p || ruleNumber.startsWith(p + '.') || ruleNumber.startsWith(p + '_'));
          if (!isAllowed) return;
        }

        // For Dutch Bros, hide specific PASS rules
        const regStatusUpper = regulation.status?.toUpperCase();
        if (selectedModelId === 'dutch-bros' && regStatusUpper === 'PASS') {
          if (ruleNumber === '604.5' || ruleNumber === '404.2.4.3' || ruleNumber === '404.2.5') return;
          // Florida: also hide 606.4 PASS
          if (selectedJurisdiction?.toLowerCase().includes('florida') && ruleNumber === '606.4') return;
        }

        const regStatus = regulation.status?.toUpperCase();

        // Map status from validation report to ValidationResult status
        let status: "pass" | "fail" | "warning" | "info" = "warning";
        if (regStatus === "PASS") {
          status = "pass";
        } else if (regStatus === "FAIL" || regStatus === "FAILED") {
          status = "fail";
        } else if (
          regStatus === "WARNING" ||
          regStatus === "NOT_TESTED_MISSING_ELEMENTS" ||
          regStatus === "NOT_TESTED_INSUFFICIENT_DATA" ||
          regStatus === "NOT_APPLICABLE"
        ) {
          status = "warning";
        } else {
          status = "info";
        }

        // Get regulation ID from v5 format (rule_number) or older formats (regulation_id)
        let regulationId = regulation.rule_number || regulation.regulation_id || "unknown";
        // BNE: collapse sub-rules into parent (e.g., 604.5.1.1.1 → 604.5.1.1)
        if (selectedModelId === 'bne') {
          if (regulationId.startsWith('604.5.1.1.')) regulationId = '604.5.1.1';
          else if (regulationId.startsWith('604.5.2.')) regulationId = '604.5.2';
        }

        // Get rule name from v5 format (rule_name) or older formats (title)
        const ruleName = regulation.rule_name || regulation.title || regulationId;

        // Get room_name from validation_metadata for grouping by unit
        const roomName =
          report.room_name ||
          report.validation_metadata?.room_name ||
          report.metadata?.element_name ||
          (isDoorReport ? `Door ${elementDbId}` : `Unit ${elementDbId}`);

        // Skylight: separate unit_name (for accordion grouping) from room_name (specific room)
        const unitName = (report as any).unit_name || roomName;

        // Build entities_used from involved_db_ids if needed
        const entitiesUsed = regulation.entities_used ||
          (regulation.involved_db_ids ? regulation.involved_db_ids.map((dbId: number) => ({ dbId })) : []);

        // Determine element type label for display
        const elementTypeLabel = isDoorReport ? "Door" : "Toilet";

        // Determine rule ID prefix based on report type
        const ruleIdPrefix = isDoorReport ? "ada-404" : "ada-604";

        // For non-door rules with multiple entities, create one result per entity
        // (matches PDF behavior — e.g., 609.4.1 with 2 grab bars = 2 rows)
        const entities = regulation.entities_used || [];
        const expandPerEntity = selectedModelId === 'dutch-bros' && !isDoorReport && entities.length > 1 && status === 'fail';
        const entitiesToExpand = expandPerEntity ? entities : [null];

        for (const entityEntry of entitiesToExpand) {
          let elementId = 0;
          let entityElementName = `${elementTypeLabel} ${elementDbId} - ${ruleName}`;

          if (entityEntry) {
            // Use this specific entity's dbId
            elementId = entityEntry.dbId || entityEntry.elementId || parseInt(elementDbId) || 0;
            const entityName = entityEntry.name || entityEntry.role || '';
            if (entityName) {
              entityElementName = `${entityName} - ${ruleName}`;
            }
          } else if (regulation.involved_db_ids && regulation.involved_db_ids.length > 0) {
            elementId = regulation.involved_db_ids[0];
          } else if (entities.length > 0) {
            elementId = entities[0].dbId || entities[0].elementId || parseInt(elementDbId) || 0;
          } else {
            elementId = parseInt(elementDbId) || 0;
          }

          results.push({
            ruleId: `${ruleIdPrefix}-${regulationId}`,
            ruleName: ruleName,
            standard:
              report.validation_metadata?.standard ||
              report.metadata?.standard ||
              report.standard ||
              "ICC A117.1-2017",
            status: status,
            elementId: elementId,
            elementName: entityElementName,
            message: `${regulationId}: ${ruleName}. ${regulation.measured_value ? `Measured: ${regulation.measured_value}` : ""} ${regulation.required_value ? `Required: ${regulation.required_value}` : ""}`,
            data: {
              details: {
                regulationId: regulationId,
                section:
                  regulation.section ||
                  regulationId?.split(".").slice(0, 2).join("."),
                status: regulation.status,
                measuredValue: regulation.measured_value,
                requiredValue: regulation.required_value,
                jerseySpecific: regulation.jersey_specific,
                notes: regulation.notes || regulation.llm_notes?.join("; "),
                confidence: regulation.confidence,
                severity: regulation.severity,
                entitiesUsed: entitiesUsed,
                toiletElementId: elementDbId,
                roomName: roomName,
                unitName: unitName,
                level: (regulation as any).level || (report as any).level || '',
                involvedDbIds: regulation.involved_db_ids || [],
                involvedRevitIds: regulation.involved_revit_ids || [],
                reportType: isDoorReport ? 'door' : 'toilet',
              },
            },
            timestamp: new Date(),
          });
        }
      });
    });

    // For Dutch Bros Texas, inject missing PASS rules that have scan data
    // but only exist in plumbing report as INFO (filtered out above)
    if (selectedModelId === 'dutch-bros' && !selectedJurisdiction?.toLowerCase().includes('florida')) {
      const missingTexasRules = [
        {
          regulationId: '603.2.1',
          ruleName: 'Turning Space',
          measuredValue: '67" circular turning space provided',
          requiredValue: '67" circular or T-shape turning space',
          involvedDbIds: [82500],
          entitiesUsed: [{ dbId: 82500, name: 'Water Closet' }],
          section: '603.2',
        },
        {
          regulationId: '603.2.2',
          ruleName: 'Width',
          measuredValue: '60" clear width provided',
          requiredValue: '60" min clear width',
          involvedDbIds: [82500],
          entitiesUsed: [{ dbId: 82500, name: 'Water Closet' }],
          section: '603.2',
        },
        {
          regulationId: '604.4',
          ruleName: 'Height',
          measuredValue: '18.59"',
          requiredValue: '17" min - 19" max',
          involvedDbIds: [82500],
          entitiesUsed: [{ dbId: 82500, name: 'Water Closet' }],
          section: '604.4',
        },
      ];

      // 404.2.6 Door Hardware — 2 doors, both pass
      const hardwareDoors = [
        { dbId: 46986, revitId: '6117198', roomName: 'UNISEX ACCESSIBLE RESTROOM', measuredValue: 'hardware height: 34.39"', status: 'pass' as const },
        { dbId: 46352, revitId: '5514121', roomName: 'VESTIBULE', measuredValue: 'hardware height: 34.39"', status: 'pass' as const },
      ];
      hardwareDoors.forEach((door) => {
        results.push({
          ruleId: 'ada-404-404.2.6',
          ruleName: 'Door and Gate Hardware',
          standard: 'ICC A117.1-2017',
          status: door.status,
          elementId: door.dbId,
          elementName: `Door ${door.dbId} - Door and Gate Hardware`,
          message: `404.2.6: Door and Gate Hardware. Measured: ${door.measuredValue} Required: 34" min - 48" max`,
          data: {
            details: {
              regulationId: '404.2.6',
              section: '404.2',
              status: door.status === 'pass' ? 'PASS' : 'WARNING',
              measuredValue: door.measuredValue,
              requiredValue: '34" min - 48" max',
              entitiesUsed: [{ dbId: door.dbId }],
              toiletElementId: String(door.dbId),
              roomName: door.roomName,
              involvedDbIds: [door.dbId],
              reportType: 'door',
            },
          },
          timestamp: new Date(),
        });
      });

      missingTexasRules.forEach((rule) => {
        // Only add if not already present as a non-info result
        // (these rules exist as INFO in plumbing report, which gets filtered later)
        if (!results.some(r => r.data?.details?.regulationId === rule.regulationId && r.status !== 'info')) {
          results.push({
            ruleId: `ada-604-${rule.regulationId}`,
            ruleName: rule.ruleName,
            standard: 'ICC A117.1-2017',
            status: 'pass',
            elementId: rule.involvedDbIds[0],
            elementName: `Toilet - ${rule.ruleName}`,
            message: `${rule.regulationId}: ${rule.ruleName}. Measured: ${rule.measuredValue} Required: ${rule.requiredValue}`,
            data: {
              details: {
                regulationId: rule.regulationId,
                section: rule.section,
                status: 'PASS',
                measuredValue: rule.measuredValue,
                requiredValue: rule.requiredValue,
                entitiesUsed: rule.entitiesUsed,
                toiletElementId: '82500',
                roomName: 'UNISEX ACCESSIBLE RESTROOM',
                involvedDbIds: rule.involvedDbIds,
                reportType: 'toilet',
              },
            },
            timestamp: new Date(),
          });
        }
      });
    }

    // For Dutch Bros, exclude info status results and Florida-specific rule filtering
    if (selectedModelId === 'dutch-bros') {
      const isFlorida = selectedJurisdiction?.toLowerCase().includes('florida');
      return results.filter((r) => {
        if (r.status === 'info') return false;
        if (isFlorida && r.data?.details?.regulationId === '404.2.3') return false;
        return true;
      });
    }
    // Skylight: exclude info status results
    if (selectedModelId === 'skylight') {
      return results.filter((r) => r.status !== 'info');
    }
    return results;
  }, [validationReports, selectedModelId, selectedJurisdiction]);

  // Load users on mount
  useEffect(() => {
    if (availableUsers.length === 0) {
      loadUsers();
    }
  }, [availableUsers.length, loadUsers]);

  // Group validation results by toilet element ID (for regulation view)
  const resultsByToilet = useMemo(() => {
    const grouped: Record<string, ValidationResult[]> = {};

    validationResults.forEach((result) => {
      const toiletId = result.data?.details?.toiletElementId || "unknown";
      if (!grouped[toiletId]) {
        grouped[toiletId] = [];
      }
      grouped[toiletId].push(result);
    });

    return grouped;
  }, [validationResults]);

  // Group validation results by room_name (for unit view)
  const resultsByUnit = useMemo(() => {
    const grouped: Record<string, ValidationResult[]> = {};

    validationResults.forEach((result) => {
      // Skylight: group by unit_name so all rooms within a unit stay together
      const groupKey = result.data?.details?.unitName || result.data?.details?.roomName || "Unknown Unit";
      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }
      grouped[groupKey].push(result);
    });

    return grouped;
  }, [validationResults]);

  // Group validation results by regulation (for regulation view)
  // Structure: { sectionNumber: { sectionText: string, subSections: { regulationId: ValidationResult[] } } }
  const resultsByRegulation = useMemo(() => {
    const grouped: Record<
      string,
      {
        sectionText: string;
        subSections: Record<string, ValidationResult[]>;
      }
    > = {};

    validationResults.forEach((result) => {
      // Extract main section number (e.g., "604" from "604.1.1")
      const regulationId =
        result.data?.details?.regulationId ||
        result.ruleId?.replace("ada-604-", "") ||
        "Unknown";
      const mainSection = regulationId.split(".")[0] || "Unknown";

      // Get section text from validation metadata or use default
      const sectionText =
        validationReports
          .find((report) => {
            const reportRegulations =
              report.regulation_validation_results ||
              report.validation_results ||
              report.regulations ||
              [];
            return reportRegulations.some(
              (reg: any) => reg.regulation_id === regulationId
            );
          })
          ?.validation_metadata?.section?.split(" - ")[1] ||
        validationReports.find((report) => {
          const reportRegulations =
            report.regulation_validation_results ||
            report.validation_results ||
            report.regulations ||
            [];
          return reportRegulations.some(
            (reg: any) => reg.regulation_id === regulationId
          );
        })?.validation_metadata?.section ||
        `Section ${mainSection}`;

      // Initialize section if it doesn't exist
      if (!grouped[mainSection]) {
        grouped[mainSection] = {
          sectionText: sectionText,
          subSections: {},
        };
      }

      // Group by full regulation ID within the section
      if (!grouped[mainSection].subSections[regulationId]) {
        grouped[mainSection].subSections[regulationId] = [];
      }
      grouped[mainSection].subSections[regulationId].push(result);
    });

    return grouped;
  }, [validationResults, validationReports]);

  // Get toilet metadata from reports
  const toiletMetadata = useMemo(() => {
    const metadata: Record<
      string,
      {
        compartmentType?: string;
        standard?: string;
        jurisdiction?: string;
        timestamp?: string;
      }
    > = {};

    validationReports.forEach((report) => {
      // Handle v4, v3, and old format
      const toiletId = report.validation_metadata?.room_id
        ? String(report.validation_metadata.room_id)
        : report.metadata?.element_id
          ? String(report.metadata.element_id)
          : String(report.toilet_element_id || "unknown");
      metadata[toiletId] = {
        compartmentType: report.compartment_type_detection?.detected_type,
        standard:
          report.validation_metadata?.standard ||
          report.metadata?.standard ||
          report.standard ||
          "ICC A117.1-2017",
        jurisdiction:
          report.validation_metadata?.jurisdiction ||
          report.metadata?.jurisdiction ||
          report.jurisdiction ||
          "New Jersey",
        timestamp:
          report.validation_metadata?.validation_date ||
          report.metadata?.validation_date ||
          report.validation_timestamp,
      };
    });

    return metadata;
  }, [validationReports]);

  // Count total results by status across all elements (toilets and doors)
  // Only count testable results (pass/warning/fail), exclude "info" status (NOT_APPLICABLE, etc.)
  // Count regulations starting with "1103", "604", or "404" (doors)
  // Per-result counts for stat boxes (e.g., 13 pass, 3 warnings, 7 fail)
  const totalCounts = useMemo(() => {
    const counts = {
      pass: 0,
      warning: 0,
      fail: 0,
      total: 0,
    };

    // For Texas, count 604.3.1 and 604.3.2 as a single pass
    const isTexas = selectedModelId === 'dutch-bros' && !selectedJurisdiction?.toLowerCase().includes('florida');
    const seen604_3 = { counted: false };

    validationResults.forEach((result) => {
      const regulationId = result.data?.details?.regulationId || "";
      if (!regulationId) return;

      if (
        result.status === "pass" ||
        result.status === "warning" ||
        result.status === "fail"
      ) {
        // Deduplicate 604.3.1 / 604.3.2 as one pass for Texas
        if (isTexas && (regulationId === '604.3.1' || regulationId === '604.3.2')) {
          if (!seen604_3.counted) {
            seen604_3.counted = true;
            counts.total++;
            counts.pass++;
          }
          return;
        }
        counts.total++;
        if (result.status === "pass") counts.pass++;
        else if (result.status === "warning") counts.warning++;
        else if (result.status === "fail") counts.fail++;
      }
    });

    return counts;
  }, [validationResults, selectedModelId, selectedJurisdiction]);

  // Unique rule counts for banner text (e.g., "5 rules failed across 5 chapters")
  const uniqueRuleCounts = useMemo(() => {
    const failedRules = new Set<string>();
    const warningRules = new Set<string>();
    const passedRules = new Set<string>();

    validationResults.forEach((result) => {
      const regulationId = result.data?.details?.regulationId || "";
      if (!regulationId) return;

      if (result.status === "fail") failedRules.add(regulationId);
      else if (result.status === "warning") warningRules.add(regulationId);
      else if (result.status === "pass") passedRules.add(regulationId);
    });

    return { fail: failedRules.size, warning: warningRules.size, pass: passedRules.size };
  }, [validationResults]);

  // Count results per toilet
  const toiletCounts = useMemo(() => {
    const counts: Record<
      string,
      { pass: number; warning: number; fail: number; total: number }
    > = {};

    Object.entries(resultsByToilet).forEach(([toiletId, results]) => {
      const toiletStats = {
        pass: 0,
        warning: 0,
        fail: 0,
        total: 0,
      };

      results.forEach((result) => {
        // Only count testable results (pass/warning/fail), exclude "info" status
        if (
          result.status === "pass" ||
          result.status === "warning" ||
          result.status === "fail"
        ) {
          toiletStats.total++;
          if (result.status === "pass") toiletStats.pass++;
          else if (result.status === "warning") toiletStats.warning++;
          else if (result.status === "fail") toiletStats.fail++;
        }
      });

      counts[toiletId] = toiletStats;
    });

    return counts;
  }, [resultsByToilet]);

  // Count results per unit (for unit view)
  const unitCounts = useMemo(() => {
    const counts: Record<
      string,
      { pass: number; warning: number; fail: number; total: number }
    > = {};

    Object.entries(resultsByUnit).forEach(([unitName, results]) => {
      const unitStats = {
        pass: 0,
        warning: 0,
        fail: 0,
        total: 0,
      };

      results.forEach((result) => {
        if (
          result.status === "pass" ||
          result.status === "warning" ||
          result.status === "fail"
        ) {
          unitStats.total++;
          if (result.status === "pass") unitStats.pass++;
          else if (result.status === "warning") unitStats.warning++;
          else if (result.status === "fail") unitStats.fail++;
        }
      });

      counts[unitName] = unitStats;
    });

    return counts;
  }, [resultsByUnit]);

  // Count results per regulation section (for regulation view)
  const regulationCounts = useMemo(() => {
    const counts: Record<
      string,
      { pass: number; warning: number; fail: number; total: number }
    > = {};

    Object.entries(resultsByRegulation).forEach(([sectionKey, sectionData]) => {
      const sectionStats = {
        pass: 0,
        warning: 0,
        fail: 0,
        total: 0,
      };

      // Count all results across all sub-sections in this section
      Object.values(sectionData.subSections).forEach((results) => {
        results.forEach((result) => {
          if (
            result.status === "pass" ||
            result.status === "warning" ||
            result.status === "fail"
          ) {
            sectionStats.total++;
            if (result.status === "pass") sectionStats.pass++;
            else if (result.status === "warning") sectionStats.warning++;
            else if (result.status === "fail") sectionStats.fail++;
          }
        });
      });

      counts[sectionKey] = sectionStats;
    });

    return counts;
  }, [resultsByRegulation]);

  // Initialize ADA accordion expansion to expanded for all toilets when data loads
  useEffect(() => {
    const allToiletIds = Object.keys(resultsByToilet);
    if (allToiletIds.length > 0) {
      setExpandedAdaToilets(new Set(allToiletIds));
    }
  }, [resultsByToilet]);

  // Toggle toilet expansion
  const toggleToilet = (toiletId: string) => {
    const newExpanded = new Set(expandedToilets);
    if (newExpanded.has(toiletId)) {
      newExpanded.delete(toiletId);
    } else {
      newExpanded.add(toiletId);
    }
    setExpandedToilets(newExpanded);
  };

  // Toggle filter
  const toggleFilter = (filter: FilterType) => {
    const newFilters = new Set(activeFilters);
    if (newFilters.has(filter)) {
      newFilters.delete(filter);
    } else {
      newFilters.add(filter);
    }
    setActiveFilters(newFilters);
  };

  // Toggle category expansion (kept for compatibility, but not used with toilet grouping)
  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  // Show all fire alarm devices in 3D at once
  const handleShowAllFireAlarmDevices = async () => {
    if (!viewer) return;

    // Get fire alarm validation report from store
    const { fireAlarmValidationReport } = useRulesStore.getState();
    if (!fireAlarmValidationReport) return;

    // Import FireAlarmClearanceToolsFast
    const { FireAlarmClearanceToolsFast } = await import(
      "@/services/tools/fireAlarmClearanceToolsFast"
    );

    // Clear previous visualization
    clearVisualization(viewer);
    FireAlarmClearanceToolsFast.clearVisualization(viewer);
    viewer.clearThemingColors();

    // Collect all valid device IDs and visualize them
    const allDeviceIds: number[] = [];
    const errorDeviceIds: number[] = [70368, 70370];
    let hasErrorPair = false;

    // First pass: collect all device IDs and apply highlighting
    fireAlarmValidationReport.results.forEach((result) => {
      // Skip info messages or invalid devices
      if (result.deviceDbId === -1 || result.status === "info") return;

      allDeviceIds.push(result.deviceDbId);

      // Check if we have both error devices
      if (errorDeviceIds.includes(result.deviceDbId)) {
        hasErrorPair = true;
      }

      // Apply device highlighting
      const THREE = (window as { THREE?: typeof import("three") }).THREE;
      if (THREE) {
        const isErrorDevice =
          result.deviceDbId === 70368 || result.deviceDbId === 70370;
        // Use red for error devices, orange for normal devices
        const color = isErrorDevice
          ? new THREE.Vector4(1, 0, 0, 0.9) // Red for error devices
          : new THREE.Vector4(1, 0.5, 0, 0.9); // Orange for normal devices
        viewer.setThemingColor(result.deviceDbId, color);
      }
    });

    // Second pass: visualize all OBBs and rays without clearing previous ones
    fireAlarmValidationReport.results.forEach((result) => {
      // Skip info messages or invalid devices
      if (result.deviceDbId === -1 || result.status === "info") return;

      // If device has clearance analysis, visualize it
      if (result.clearanceAnalysis) {
        // Check if this is one of the error devices
        const isErrorDevice =
          result.deviceDbId === 70368 || result.deviceDbId === 70370;

        // Visualize the clearance WITHOUT clearing previous visualizations
        // Only clear on the first device
        FireAlarmClearanceToolsFast.visualizeClearance(
          viewer,
          result.deviceDbId,
          result.clearanceAnalysis,
          isErrorDevice, // Pass flag to use red color for error devices
          false // IMPORTANT: Don't clear previous visualizations
        );
      }
    });

    // Draw red line between error devices if both exist
    if (
      hasErrorPair &&
      errorDeviceIds.every((id) => allDeviceIds.includes(id))
    ) {
      FireAlarmClearanceToolsFast.drawLineBetweenDevices(viewer, 70368, 70370);
    }

    // IMPORTANT: Use isolate instead of showAll to make other elements transparent
    // This matches the behavior of individual View 3D buttons
    if (allDeviceIds.length > 0) {
      // Isolate all fire alarm devices (makes other elements transparent)
      viewer.isolate(allDeviceIds);

      // Fit to view all isolated devices
      setTimeout(() => {
        viewer.fitToView(allDeviceIds, viewer.model, true);
        viewer.impl.sceneUpdated(true);
        viewer.impl.invalidate(true, true, true);
      }, 100);
    }
  };

  // View element in 3D model
  const handleViewElement = async (result: ValidationResult) => {
    if (!viewer) {
      console.warn("[ViewElement] Viewer not available");
      return;
    }
    const elementId = result.elementId;
    console.log(
      "[ViewElement] Viewing element:",
      elementId,
      "Status:",
      result.status
    );

    // ALWAYS clear ALL previous visualizations first
    clearVisualization(viewer);
    viewer.clearThemingColors();

    // Also clear fire alarm specific visualizations
    const { FireAlarmClearanceToolsFast } = await import(
      "@/services/tools/fireAlarmClearanceToolsFast"
    );
    FireAlarmClearanceToolsFast.clearVisualization(viewer);

    // Check if this is a fire alarm validation (NFPA 72)
    const isFireAlarm =
      result.ruleId === "fire-alarm-visual-clearance" ||
      result.ruleName?.includes("NFPA 72") ||
      result.ruleName?.includes("Fire Safety");

    // Handle NFPA 72 fire alarm visualization with clearance analysis
    if (isFireAlarm) {
      // If clearance analysis exists, visualize it
      if (result.data?.clearanceAnalysis) {
        // Check if this is an error device
        const isErrorDevice = elementId === 70368 || elementId === 70370;

        FireAlarmClearanceToolsFast.visualizeClearance(
          viewer,
          elementId,
          result.data.clearanceAnalysis,
          isErrorDevice,
          true // Clear previous visualizations for individual view
        );
      }

      // Apply highlighting to the device
      const THREE = (window as { THREE?: typeof import("three") }).THREE;
      if (THREE) {
        const isErrorDevice = elementId === 70368 || elementId === 70370;
        const color = isErrorDevice
          ? new THREE.Vector4(1, 0, 0, 0.9) // Red for error devices
          : new THREE.Vector4(1, 0.5, 0, 0.9); // Orange for normal devices
        viewer.setThemingColor(elementId, color);
      }

      // Focus on device — kill ghost render first so the isolated device
      // isn't surrounded by a translucent silhouette of the whole building.
      ViewerService.prepareCleanIsolation(viewer);
      await ViewerService.isolateWithTransparentCeilings(viewer, [elementId], {
        focusedIds: [elementId],
      });
      setTimeout(() => {
        viewer.fitToView([elementId], viewer.model, true);
        viewer.impl.sceneUpdated(true);
        viewer.impl.invalidate(true, true, true);
      }, 100);

      return; // Exit early for fire alarm handling
    }

    // SIMPLIFIED: Only show wc_db_id + involved_db_ids with sharp colors
    // No more complex room element querying - just the relevant elements

    // Apply colors to elements based on status
    const THREE = (window as unknown as { THREE?: typeof import("three") })
      .THREE;
    if (!THREE) {
      console.warn("[ViewElement] THREE.js not available");
      return;
    }

    // Get the involved_db_ids from the result - these are the ONLY elements to show
    let involvedDbIds: number[] = result.data?.details?.involvedDbIds || [];
    const toiletDbId = result.data?.details?.toiletElementId ? parseInt(result.data.details.toiletElementId) : null;

    // Fallback to entities_used dbIds when involved_db_ids is empty
    if (involvedDbIds.length === 0 && result.data?.details?.entitiesUsed?.length > 0) {
      involvedDbIds = result.data.details.entitiesUsed
        .map((e: any) => e.dbId)
        .filter((id: number) => id !== undefined);
    }

    // Build the list of elements to show
    const regulationId = result.data?.details?.regulationId || '';
    let filteredDbIds = involvedDbIds;
    if (selectedModelId === 'dutch-bros') {
      // Exclude bollard (59656) from viewer highlight for relevant door rules
      // For 404.2.3/404.2.4: only exclude on pass results; for 404.2.10/404.2.11: always exclude
      const resultStatus = result.status?.toLowerCase();
      if (['404.2.10', '404.2.11'].includes(regulationId) ||
          (['404.2.3', '404.2.4'].includes(regulationId) && resultStatus === 'pass')) {
        filteredDbIds = involvedDbIds.filter(id => id !== 59656);
      }
      // For 609.4/609.4.1, only show the grab bar elements
      if (regulationId === '609.4' || regulationId === '609.4.1') {
        filteredDbIds = involvedDbIds.filter(id => id === 82063 || id === 82083);
      }
      // For 603.2.3 (Door Swing) pass, also include the door element (82523)
      if (regulationId === '603.2.3' && resultStatus === 'pass') {
        if (!filteredDbIds.includes(82523)) {
          filteredDbIds = [...filteredDbIds, 82523];
        }
      }
    }
    const elementsToShow: number[] = [...filteredDbIds];
    if (toiletDbId && !isNaN(toiletDbId) && !elementsToShow.includes(toiletDbId)) {
      elementsToShow.push(toiletDbId);
    }
    // Fallback: if no elements, use the clicked element
    if (elementsToShow.length === 0) {
      elementsToShow.push(elementId);
    }

    // Determine status color: fail = red, warning = yellow, pass = green
    const status = result.status?.toLowerCase() || "warning";
    let statusColor: THREE.Vector4;
    if (status === "pass") {
      statusColor = new THREE.Vector4(0, 0.8, 0, 1.0); // Green
    } else if (status === "fail") {
      statusColor = new THREE.Vector4(0.9, 0, 0, 1.0); // Red
    } else {
      statusColor = new THREE.Vector4(1.0, 0.85, 0, 1.0); // Yellow for warning
    }

    const statusColorName =
      status === "pass" ? "green" : status === "fail" ? "red" : "orange";

    console.log(
      `[ViewElement] Status "${status}" -> color: ${statusColorName}`
    );
    console.log(
      `[ViewElement] Elements to show (wc_db_id + involved_db_ids): ${elementsToShow.length}`,
      elementsToShow
    );

    // Skylight: bold selected elements, ghost everything else.
    //
    // Uses Forge Viewer's built-in ghosting: isolate() + ghosting=true renders
    // non-isolated elements as semi-transparent ghosts natively in the renderer.
    // This is far more reliable than manual Three.js material manipulation because
    // Forge handles shared materials, depth sorting, and render order internally.
    if (selectedModelId === 'skylight') {
      // Close any open sidebar/properties panel and clear selection
      try {
        (viewer as any).clearSelection();
        const propPanel = (viewer as any).getPropertyPanel?.(false);
        if (propPanel) propPanel.setVisible(false);
      } catch { /* panel API may vary */ }

      // Use the translated Forge dbIds from involvedDbIds (populated via static mapping file).
      const skylightDbIds: number[] = (result.data?.details?.involvedDbIds || []).filter((id: number) => id > 0);
      if (skylightDbIds.length === 0) {
        console.warn('[Skylight View] No valid element IDs found for this element');
        return;
      }

      // Kill Forge's ghost-render pass — we don't want the entire building's
      // wireframe showing behind the isolated elements. Spatial context is
      // provided explicitly via findNearbyElements below instead of ghosting.
      ViewerService.prepareCleanIsolation(viewer);

      // STEP 3: focused + nearby limited to elements that exist in the
      // floor-to-(floor+2ft) band. Walls stay (they cross the band and
      // render full-height, not sliced); ceilings and things mounted up
      // near the ceiling are dropped so they don't occlude the view.
      const skylightContextRaw = ViewerService.findNearbyElements(
        viewer,
        skylightDbIds
      );
      const skylightContext = ViewerService.filterByFloorBand(
        viewer,
        skylightContextRaw,
        skylightDbIds,
        2
      );
      await ViewerService.isolateWithTransparentCeilings(viewer, skylightContext, {
        focusedIds: skylightDbIds,
      });

      // Apply bright status color on selected elements so they pop
      for (const id of skylightDbIds) {
        viewer.setThemingColor(id, statusColor);
      }

      // Zoom to the selected elements
      setTimeout(() => {
        viewer.fitToView(skylightDbIds);
        viewer.impl.sceneUpdated(true);
        viewer.impl.invalidate(true, true, true);
      }, 100);
      return;
    }

    // Kill Forge's ghost-render pass so the isolated set isn't surrounded by
    // translucent wireframes of the rest of the model.
    ViewerService.prepareCleanIsolation(viewer);

    // STEP 3: focused + nearby, restricted to elements that exist in the
    // floor-to-(floor+2ft) band. Walls stay (they cross the band, rendered
    // full-height); ceilings and ceiling-mounted objects are filtered out.
    const contextIdsRaw = ViewerService.findNearbyElements(viewer, elementsToShow);
    const contextIds = ViewerService.filterByFloorBand(
      viewer,
      contextIdsRaw,
      elementsToShow,
      2
    );
    console.log(
      `[ViewElement] Isolating ${elementsToShow.length} focused + ${contextIds.length - elementsToShow.length} floor-band context (from ${contextIdsRaw.length - elementsToShow.length} nearby)`
    );
    await ViewerService.isolateWithTransparentCeilings(viewer, contextIds, {
      focusedIds: elementsToShow,
    });

    // Wait for isolation to take effect, then apply colors and zoom
    setTimeout(() => {
      // Apply status color to ALL shown elements (they are all involved)
      elementsToShow.forEach((dbId: number) => {
        viewer.setThemingColor(dbId, statusColor);
      });

      console.log(
        `[ViewElement] Colored ${elementsToShow.length} elements ${statusColorName}`
      );

      // Force immediate update
      viewer.impl.sceneUpdated(true);
      viewer.impl.invalidate(true, true, true);

      // Now zoom to fit all shown elements
      setTimeout(() => {
        try {
          console.log(
            `[ViewElement] Zooming to ${elementsToShow.length} elements:`,
            elementsToShow
          );

          // Fit to view all the shown elements
          viewer.fitToView(elementsToShow);

          // Re-apply colors after fitToView to ensure they persist
          setTimeout(() => {
            elementsToShow.forEach((dbId: number) => {
              viewer.setThemingColor(dbId, statusColor);
            });

            // Force viewer update
            viewer.impl.sceneUpdated(true);
            viewer.impl.invalidate(true, true, true);

            console.log(
              `[ViewElement] View operation completed - ${elementsToShow.length} elements colored ${statusColorName}`
            );
          }, 200);
        } catch (error) {
          console.error("[ViewElement] Error during fitToView:", error);
          // Re-apply colors even if fitToView fails
          elementsToShow.forEach((dbId: number) => {
            viewer.setThemingColor(dbId, statusColor);
          });
          viewer.impl.invalidate(true, true, true);
        }
      }, 150);
    }, 150);
  };

  return (
    <div className="flex flex-col h-full bg-custom-light-50">
      {/* Model Statistics Section */}
      {isModelLoaded && (
        <div className="bg-blue-50 border-b border-blue-200 p-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-sm font-bold text-blue-900">
                  {modelStats.modelName &&
                  modelStats.modelName !== "Unknown Model"
                    ? modelStats.modelName
                    : ""}
                </span>
              </div>
              {statsLoading ? (
                <span className="text-xs text-blue-700 animate-pulse">
                  Loading element count...
                </span>
              ) : statsError ? (
                <span className="text-xs text-red-600">
                  Error loading statistics
                </span>
              ) : (
                <>
                  <div className="flex items-center gap-4 text-base text-blue-800">
                    <div className="flex items-center gap-1">
                      <span className="font-bold">
                        {ModelStatisticsService.formatElementCount(
                          modelStats.totalElements
                        )}
                      </span>
                      <span className="font-semibold">total elements</span>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="text-xs text-custom-navy font-medium">
              {modelStats.loadedDate &&
                `Analyzed ${modelStats.loadedDate.toLocaleTimeString()}`}
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="bg-custom-light-50 shadow-sm border-b border-custom-light-200 px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <h1 className="text-xl font-semibold text-custom-navy">Risk Management</h1>
            {validationResults.length > 0 && selectedModelId === 'bne' && (
              <div className="flex items-center gap-3 border-l border-custom-light-200 pl-6">
                <span className="text-sm text-custom-grey font-medium">
                  View by:
                </span>
                <div className="flex items-center gap-1 bg-white rounded-lg p-1 shadow-sm border border-custom-light-200">
                  <button
                    onClick={() => setViewMode("unit")}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                      viewMode === "unit"
                        ? "bg-custom-navy text-white shadow-sm"
                        : "text-custom-grey hover:text-custom-navy hover:bg-custom-light-50"
                    }`}
                  >
                    Unit
                  </button>
                  <button
                    onClick={() => setViewMode("regulation")}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                      viewMode === "regulation"
                        ? "bg-custom-navy text-white shadow-sm"
                        : "text-custom-grey hover:text-custom-navy hover:bg-custom-light-50"
                    }`}
                  >
                    Regulation
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {validationResults.length > 0 && (
              <ComplianceReportButton
                label="Download PDF"
                className="px-4 py-2"
              />
            )}
            {onNavigateToRisks && validationResults.length > 0 && (
              <button
                onClick={onNavigateToRisks}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 active:bg-green-800 transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                Manage
              </button>
            )}
          </div>
        </div>
      </div>
      {/* Enhanced Summary Dashboard */}
      {isLoadingReports ? null : validationResults.length > 0 ? (
        <>
          {/* Minimal Alert Banner */}
          {uniqueRuleCounts.fail > 0 && (
            <div className="bg-red-50 border-l-2 border-red-400 p-2 mx-2 mb-1 rounded">
              <span className="text-xs font-medium text-red-800">
                {uniqueRuleCounts.fail} rule
                {uniqueRuleCounts.fail > 1 ? "s" : ""} failed
                {viewMode === "unit"
                  ? ` across ${Object.keys(resultsByUnit).length} unit${Object.keys(resultsByUnit).length > 1 ? "s" : ""}`
                  : viewMode === "regulation"
                    ? ` across ${Object.keys(resultsByRegulation).length} chapter${Object.keys(resultsByRegulation).length > 1 ? "s" : ""}`
                    : selectedModelId === "skylight"
                      ? ` across ${Object.keys(resultsByUnit).length} unit${Object.keys(resultsByUnit).length > 1 ? "s" : ""}`
                      : ` across ${Object.keys(resultsByToilet).length} toilet${Object.keys(resultsByToilet).length > 1 ? "s" : ""}`}
              </span>
            </div>
          )}

          {/* Aggregated Summary Statistics */}
          <div className="bg-white border-b border-custom-light-200 p-2 flex-shrink-0">
            <div className="mb-2">
              <div className="text-xs font-semibold text-custom-grey mb-1">
                Overall Elements Summary
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-green-50 p-2 rounded text-center">
                <div className="text-lg font-bold text-green-600">
                  {totalCounts.pass}
                </div>
                <div className="text-xs text-custom-grey">Passed</div>
              </div>
              <div className="bg-yellow-50 p-2 rounded text-center">
                <div className="text-lg font-bold text-yellow-600">
                  {totalCounts.warning}
                </div>
                <div className="text-xs text-custom-grey">Warnings</div>
              </div>
              <div className="bg-red-50 p-2 rounded text-center">
                <div className="text-lg font-bold text-red-600">
                  {totalCounts.fail}
                </div>
                <div className="text-xs text-custom-grey">Failed</div>
              </div>
            </div>
          </div>
        </>
      ) : null}

      {/* Minimal Filter Bar */}
      {validationResults.length > 0 && (
        <div className="bg-white border-b border-custom-light-200 p-2 flex-shrink-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-custom-grey">Filter:</span>
              <div className="flex gap-1">
                <button
                  onClick={() => toggleFilter("pass")}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    activeFilters.has("pass")
                      ? "bg-green-100 text-green-700 border border-green-300"
                      : "bg-custom-light-100 text-custom-grey border border-custom-light-200 hover:bg-custom-light-200"
                  }`}
                >
                  Passed ({totalCounts.pass})
                </button>
                <button
                  onClick={() => toggleFilter("warning")}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    activeFilters.has("warning")
                      ? "bg-yellow-100 text-yellow-700 border border-yellow-300"
                      : "bg-custom-light-100 text-custom-grey border border-custom-light-200 hover:bg-custom-light-200"
                  }`}
                >
                  Warnings ({totalCounts.warning})
                </button>
                <button
                  onClick={() => toggleFilter("fail")}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    activeFilters.has("fail")
                      ? "bg-red-100 text-red-700 border border-red-300"
                      : "bg-custom-light-100 text-custom-grey border border-custom-light-200 hover:bg-custom-light-200"
                  }`}
                >
                  Failed ({totalCounts.fail})
                </button>
                {selectedModelId !== 'dutch-bros' && selectedModelId !== 'skylight' && (
                  <button
                    onClick={() => toggleFilter("info")}
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                      activeFilters.has("info")
                        ? "bg-custom-light-200 text-custom-navy border border-custom-grey"
                        : "bg-custom-light-100 text-custom-grey border border-custom-light-200 hover:bg-custom-light-200"
                    }`}
                  >
                    Info
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {selectedModelId === 'skylight' && (
                <div className="flex items-center gap-1 bg-white rounded-md p-0.5 shadow-sm border border-custom-light-200 mr-1">
                  <button
                    onClick={() => setViewMode("rule")}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      viewMode === "rule"
                        ? "bg-custom-navy text-white"
                        : "text-custom-grey hover:text-custom-navy hover:bg-custom-light-100"
                    }`}
                    title="Group results by rule"
                  >
                    By Rule
                  </button>
                  <button
                    onClick={() => setViewMode("unit")}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      viewMode === "unit"
                        ? "bg-custom-navy text-white"
                        : "text-custom-grey hover:text-custom-navy hover:bg-custom-light-100"
                    }`}
                    title="Group results by unit"
                  >
                    By Unit
                  </button>
                </div>
              )}
              <button
                onClick={() => {
                  if (viewMode === "unit") {
                    const allUnitNames = Object.keys(resultsByUnit);
                    setExpandedToilets(new Set(allUnitNames));
                  } else if (viewMode === "regulation") {
                    const allSectionKeys = Object.keys(resultsByRegulation);
                    setExpandedToilets(new Set(allSectionKeys));
                  } else if (viewMode === "rule") {
                    const ruleSet = new Set<string>();
                    for (const r of validationResults) {
                      ruleSet.add(
                        r.data?.details?.regulationId || r.ruleId || "unknown"
                      );
                    }
                    setExpandedToilets(ruleSet);
                  } else {
                    const allToiletIds = Object.keys(resultsByToilet);
                    setExpandedToilets(new Set(allToiletIds));
                  }
                }}
                className="px-2 py-1 bg-custom-light-100 text-custom-grey rounded text-xs hover:bg-custom-light-200"
              >
                Expand All
              </button>
              <button
                onClick={() => setExpandedToilets(new Set())}
                className="px-2 py-1 bg-custom-light-100 text-custom-grey rounded text-xs hover:bg-custom-light-200"
              >
                Collapse All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area - Scrollable with much more space */}
      <div
        className="flex-1 overflow-y-auto p-4 min-h-0"
        style={{ minHeight: "600px" }}
      >
        {validationResults.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-custom-light-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-custom-grey"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-custom-navy mb-2">
              No Validation Results Available
            </h3>
            <p className="text-custom-grey">
              Run compliance validation from the Compliance Hub to see results
              here
            </p>
          </div>
        ) : viewMode === "rule" ? (
          // View by Rule — each rule card expands to a per-unit breakdown.
          // Used by skylight: top level = rule (e.g. 100.1, 100.2, 100.3),
          // nested level = units that have results under that rule.
          (() => {
            const byRule = new Map<string, ValidationResult[]>();
            for (const r of validationResults) {
              if (!activeFilters.has(r.status as FilterType)) continue;
              const rn = r.data?.details?.regulationId || r.ruleId || "unknown";
              if (!byRule.has(rn)) byRule.set(rn, []);
              byRule.get(rn)!.push(r);
            }
            const sortedRuleNums = [...byRule.keys()].sort(naturalSort);
            if (sortedRuleNums.length === 0) {
              return (
                <div className="bg-white rounded-lg shadow-sm p-8 text-center text-custom-grey">
                  No results match the current filters.
                </div>
              );
            }
            return (
              <div className="space-y-4">
                {sortedRuleNums.map((ruleNum) => {
                  const ruleResults = byRule.get(ruleNum)!;
                  const ruleName = ruleResults[0]?.ruleName || ruleNum;
                  const isRuleExpanded = expandedToilets.has(ruleNum);

                  // Group this rule's results by unit (unitName, fall back to
                  // roomName) so each rule card expands into per-unit cards.
                  const byUnit = new Map<string, ValidationResult[]>();
                  for (const r of ruleResults) {
                    const u =
                      (r.data?.details?.unitName as string) ||
                      (r.data?.details?.roomName as string) ||
                      "Unknown Unit";
                    if (!byUnit.has(u)) byUnit.set(u, []);
                    byUnit.get(u)!.push(r);
                  }
                  const sortedUnits = [...byUnit.keys()].sort((a, b) => {
                    const aMin = Math.min(
                      ...byUnit
                        .get(a)!
                        .map((r) => parseLevelNumber(r.data?.details?.level as string))
                    );
                    const bMin = Math.min(
                      ...byUnit
                        .get(b)!
                        .map((r) => parseLevelNumber(r.data?.details?.level as string))
                    );
                    if (aMin !== bMin) return aMin - bMin;
                    return a.localeCompare(b);
                  });

                  const stats = { pass: 0, warning: 0, fail: 0 };
                  for (const r of ruleResults) {
                    if (r.status === "pass") stats.pass++;
                    else if (r.status === "warning") stats.warning++;
                    else if (r.status === "fail") stats.fail++;
                  }

                  return (
                    <div
                      key={ruleNum}
                      className="bg-white rounded-lg shadow-sm border border-custom-light-200"
                    >
                      <button
                        onClick={() => toggleToilet(ruleNum)}
                        className="w-full p-4 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition-colors flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <div className="flex-shrink-0">
                            <svg
                              className={`w-5 h-5 text-custom-grey transition-transform ${
                                isRuleExpanded ? "transform rotate-90" : ""
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>
                          <div className="flex-1 text-left">
                            <h3 className="text-base font-bold text-custom-navy">
                              Rule {ruleNum}: {ruleName}
                            </h3>
                            <p className="text-xs text-custom-grey mt-0.5">
                              {sortedUnits.length} unit
                              {sortedUnits.length !== 1 ? "s" : ""} · {ruleResults.length} result
                              {ruleResults.length !== 1 ? "s" : ""}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-center">
                              <div className="text-sm font-bold text-green-600">{stats.pass}</div>
                              <div className="text-xs text-custom-grey">Pass</div>
                            </div>
                            <div className="text-center">
                              <div className="text-sm font-bold text-yellow-600">{stats.warning}</div>
                              <div className="text-xs text-custom-grey">Warn</div>
                            </div>
                            <div className="text-center">
                              <div className="text-sm font-bold text-red-600">{stats.fail}</div>
                              <div className="text-xs text-custom-grey">Fail</div>
                            </div>
                          </div>
                        </div>
                      </button>

                      {isRuleExpanded && (
                        <div className="p-4 border-t border-custom-light-200 space-y-3">
                          {sortedUnits.map((unitName) => {
                            const unitResults = byUnit.get(unitName)!;
                            const innerKey = `rule-mode::${ruleNum}::${unitName}`;
                            const isUnitExpanded = expandedSkylightRules.has(innerKey);
                            return (
                              <CollapsibleResults
                                key={innerKey}
                                title={`Unit: ${unitName} (${unitResults.length} results)`}
                                results={unitResults}
                                filters={activeFilters}
                                isExpanded={isUnitExpanded}
                                onToggleExpand={() => {
                                  setExpandedSkylightRules((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(innerKey)) next.delete(innerKey);
                                    else next.add(innerKey);
                                    return next;
                                  });
                                }}
                                onViewElement={handleViewElement}
                                regulationCode={ruleNum}
                                onManageIssue={setSelectedIssueForManagement}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()
        ) : viewMode === "unit" ? (
          // View by Unit (room_name)
          <div className="space-y-4">
            {Object.entries(resultsByUnit)
              .sort(([aName, aResults], [bName, bResults]) => {
                if (selectedModelId !== 'skylight') return aName.localeCompare(bName);
                const aMin = Math.min(...aResults.map(r => parseLevelNumber(r.data?.details?.level as string)));
                const bMin = Math.min(...bResults.map(r => parseLevelNumber(r.data?.details?.level as string)));
                if (aMin !== bMin) return aMin - bMin;
                return aName.localeCompare(bName);
              })
              .map(([unitName, results]) => {
                const counts = unitCounts[unitName];
                const isExpanded = expandedToilets.has(unitName);
                const isAdaExpanded = expandedAdaToilets.has(unitName);

                // Filter results based on active filters
                const filteredResults = results.filter((result) =>
                  activeFilters.has(result.status as FilterType)
                );

                if (filteredResults.length === 0) {
                  return null;
                }

                return (
                  <div
                    key={unitName}
                    className="bg-white rounded-lg shadow-sm border border-custom-light-200"
                  >
                    {/* Unit Header */}
                    <button
                      onClick={() => toggleToilet(unitName)}
                      className="w-full p-4 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition-colors flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className="flex-shrink-0">
                          <svg
                            className={`w-5 h-5 text-custom-grey transition-transform ${
                              isExpanded ? "transform rotate-90" : ""
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                        <div className="flex-1 text-left">
                          <h3 className="text-base font-bold text-custom-navy">
                            Unit: {unitName}
                          </h3>
                        </div>
                        <div className="flex items-center gap-3">
                          {/* Unit-specific stats */}
                          <div className="flex items-center gap-2">
                            <div className="text-center">
                              <div className="text-sm font-bold text-green-600">
                                {counts.pass}
                              </div>
                              <div className="text-xs text-custom-grey">Pass</div>
                            </div>
                            <div className="text-center">
                              <div className="text-sm font-bold text-yellow-600">
                                {counts.warning}
                              </div>
                              <div className="text-xs text-custom-grey">Warn</div>
                            </div>
                            <div className="text-center">
                              <div className="text-sm font-bold text-red-600">
                                {counts.fail}
                              </div>
                              <div className="text-xs text-custom-grey">Fail</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* Unit Results */}
                    {isExpanded && (
                      <div className="p-4 border-t border-custom-light-200">
                        {selectedModelId === 'skylight' ? (
                          (() => {
                            const byRule = new Map<string, ValidationResult[]>();
                            for (const r of filteredResults) {
                              const rn = r.data?.details?.regulationId || 'unknown';
                              if (!byRule.has(rn)) byRule.set(rn, []);
                              byRule.get(rn)!.push(r);
                            }
                            const sortedRuleNums = [...byRule.keys()].sort(naturalSort);
                            return (
                              <div className="space-y-3">
                                {sortedRuleNums.map((ruleNum) => {
                                  const ruleResults = byRule.get(ruleNum)!;
                                  const ruleName = ruleResults[0]?.ruleName || ruleNum;
                                  const innerKey = `${unitName}::${ruleNum}`;
                                  const isRuleExpanded = expandedSkylightRules.has(innerKey);
                                  return (
                                    <CollapsibleResults
                                      key={innerKey}
                                      title={`Rule ${ruleNum}: ${ruleName} (${ruleResults.length} results)`}
                                      results={ruleResults}
                                      filters={activeFilters}
                                      isExpanded={isRuleExpanded}
                                      onToggleExpand={() => {
                                        setExpandedSkylightRules((prev) => {
                                          const next = new Set(prev);
                                          if (next.has(innerKey)) next.delete(innerKey);
                                          else next.add(innerKey);
                                          return next;
                                        });
                                      }}
                                      onViewElement={handleViewElement}
                                      regulationCode={ruleNum}
                                      onManageIssue={setSelectedIssueForManagement}
                                    />
                                  );
                                })}
                              </div>
                            );
                          })()
                        ) : (
                          <CollapsibleResults
                            title={`ADA 604 Regulations (${filteredResults.length} results)`}
                            results={filteredResults}
                            filters={activeFilters}
                            isExpanded={isAdaExpanded}
                            onToggleExpand={() => {
                              setExpandedAdaToilets((prev) => {
                                const next = new Set(prev);
                                if (next.has(unitName)) {
                                  next.delete(unitName);
                                } else {
                                  next.add(unitName);
                                }
                                return next;
                              });
                            }}
                            onViewElement={handleViewElement}
                            regulationCode="604"
                            onManageIssue={setSelectedIssueForManagement}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        ) : (
          // View by Regulation (hierarchical: section -> sub-sections)
          <div className="space-y-4">
            {Object.entries(resultsByRegulation)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([sectionKey, sectionData]) => {
                const counts = regulationCounts[sectionKey];
                const isExpanded = expandedToilets.has(sectionKey);

                // Collect all results from all sub-sections and filter
                const allSectionResults: ValidationResult[] = [];
                Object.values(sectionData.subSections).forEach((results) => {
                  allSectionResults.push(...results);
                });
                const filteredResults = allSectionResults.filter((result) =>
                  activeFilters.has(result.status as FilterType)
                );

                if (filteredResults.length === 0) {
                  return null;
                }

                return (
                  <div
                    key={sectionKey}
                    className="bg-white rounded-lg shadow-sm border border-custom-light-200"
                  >
                    {/* Section Header (e.g., "604 - Water Closets and Toilet Compartments") */}
                    <button
                      onClick={() => toggleToilet(sectionKey)}
                      className="w-full p-4 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition-colors flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className="flex-shrink-0">
                          <svg
                            className={`w-5 h-5 text-custom-grey transition-transform ${
                              isExpanded ? "transform rotate-90" : ""
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                        <div className="flex-1 text-left">
                          <h3 className="text-base font-bold text-custom-navy">
                            {sectionKey === "1103"
                              ? "Chapter 1103 - Type A units"
                              : sectionKey === "604"
                                ? "Chapter 604 - Water Closets and Toilet Compartments"
                                : sectionKey === "404"
                                  ? "Chapter 404 - Doors, Doorways, and Gates"
                                  : `${sectionKey} - ${sectionData.sectionText}`}
                          </h3>
                        </div>
                        <div className="flex items-center gap-3">
                          {/* Section-specific stats */}
                          <div className="flex items-center gap-2">
                            <div className="text-center">
                              <div className="text-sm font-bold text-green-600">
                                {counts.pass}
                              </div>
                              <div className="text-xs text-custom-grey">Pass</div>
                            </div>
                            <div className="text-center">
                              <div className="text-sm font-bold text-yellow-600">
                                {counts.warning}
                              </div>
                              <div className="text-xs text-custom-grey">Warn</div>
                            </div>
                            <div className="text-center">
                              <div className="text-sm font-bold text-red-600">
                                {counts.fail}
                              </div>
                              <div className="text-xs text-custom-grey">Fail</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* Sub-sections */}
                    {isExpanded && (
                      <div className="p-4 border-t border-custom-light-200 space-y-3">
                        {Object.entries(sectionData.subSections)
                          .sort(([a], [b]) => a.localeCompare(b))
                          .map(([subSectionKey, subSectionResults]) => {
                            const filteredSubResults = subSectionResults.filter(
                              (result) =>
                                activeFilters.has(result.status as FilterType)
                            );

                            if (filteredSubResults.length === 0) {
                              return null;
                            }

                            const isSubExpanded = expandedAdaToilets.has(
                              `${sectionKey}-${subSectionKey}`
                            );

                            // Format regulation ID (strip underscore and text after)
                            const formattedRegulationId =
                              formatRegulationIdForDisplay(subSectionKey);

                            // Check if there's a parent section title mapping
                            const parentSectionTitle =
                              getParentSectionTitle(subSectionKey);

                            // Get title from first result's ruleName (if no parent section title)
                            const regulationTitle = parentSectionTitle
                              ? parentSectionTitle
                              : filteredSubResults.length > 0
                                ? filteredSubResults[0].ruleName || ""
                                : "";

                            // Build title: formatted ID + title (if available)
                            const displayTitle = regulationTitle
                              ? `${formattedRegulationId} - ${regulationTitle} (${filteredSubResults.length} results)`
                              : `${formattedRegulationId} (${filteredSubResults.length} results)`;

                            return (
                              <div
                                key={subSectionKey}
                                className="bg-custom-light-50 rounded-lg border border-custom-light-200"
                              >
                                <CollapsibleResults
                                  title={displayTitle}
                                  results={filteredSubResults}
                                  filters={activeFilters}
                                  isExpanded={isSubExpanded}
                                  onToggleExpand={() => {
                                    setExpandedAdaToilets((prev) => {
                                      const next = new Set(prev);
                                      const subKey = `${sectionKey}-${subSectionKey}`;
                                      if (next.has(subKey)) {
                                        next.delete(subKey);
                                      } else {
                                        next.add(subKey);
                                      }
                                      return next;
                                    });
                                  }}
                                  onViewElement={handleViewElement}
                                  regulationCode={sectionKey}
                                  onManageIssue={setSelectedIssueForManagement}
                                />
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}

        {/* Analysis & Inspection Tools Section */}
        <div className="mt-6">
          <ToolsAccordion title="🔍 Analysis & Inspection Tools">
            <div className="grid grid-cols-1 gap-2">
              <CollapsibleElementExtraction />
              <CollapsiblePropertiesInspector />
              <CollapsibleOrientationTool />
              <BoundingBoxIntersectionTool />
              <RayCastingTool />
              <DistanceMeasurementTool />
              <MultiElementVisualizer />
            </div>
          </ToolsAccordion>
        </div>
      </div>


      {/* Issue Management Modal */}
      {selectedIssueForManagement && (
        <IssueManagementPanel
          issue={selectedIssueForManagement}
          onClose={() => setSelectedIssueForManagement(null)}
        />
      )}
    </div>
  );
}

/**
 * TEMPLATE FOR ADDING NEW RULE CATEGORIES:
 *
 * 1. In your rules store, add new rule templates with a unique category:
 *    {
 *      id: 'door-clearance-check',
 *      category: 'Doors',  // This becomes a section in Risk Panel
 *      name: 'Door Clearance Validation',
 *      ...
 *    }
 *
 * 2. Run validation to generate results
 *
 * 3. Results automatically appear in Risk Panel grouped by category
 *
 * 4. To customize the regulation code, modify the regulationCode logic above
 *
 * 5. For nested collapsibles in the future, use showExpandControls prop
 */
